import { useMemo, useState } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import Spinner from "../../../../components/Spinners";
import { ProblemBlock } from "./ProblemCard";
import { CollapsedBrief } from "./CollapsedBrief";
import { TileLabel } from "./SectionCard";
import { SilenceModal } from "./modals/SilenceModal";
import { AcceptConditionModal } from "./modals/AcceptConditionModal";
import { StepNoteModal } from "./modals/StepNoteModal";
import { ExecuteStepModal } from "./modals/ExecuteStepModal";
import { NoteModal } from "./modals/NoteModal";
import { LogFixModal } from "./modals/LogFixModal";
import { HandFixModal } from "./modals/HandFixModal";
import { EditEntryModal } from "./modals/EditEntryModal";
import { RemoveHistoryRowModal } from "./modals/RemoveHistoryRowModal";
import {
  useAddCondition,
  useClearSilence,
  useEditIncidentNote,
  useFixLog,
  useIncident,
  useIncidentTransition,
  useInvestigate,
  useLogNote,
  usePlanStepAction,
  useRemoveHistoryRow,
  useSilenceIncident,
} from "../hooks/useTrackingHealth";
import { fmtActor, fmtDate } from "../format";
import { hotelCopyContext } from "../problem-copy";
import { PROBLEM_SHORT_LABELS, SILENCE_CAUSE_DISPLAY } from "../vocabulary";
import type {
  FixStepAction,
  HotelDetail,
  IncidentDetail,
  IncidentEventRow,
  ProblemId,
  PlanProblemView,
  PlanStepView,
  RuleRow,
} from "../types";

/**
 * The incident, flattened onto the hotel page — there is no separate incident
 * page any more, so this IS the thread: the problems, the fix plan and its
 * buttons, the verification checklist, the blocked-on log, the investigation
 * brief, in ONE card.
 *
 * **One card per problem** (`ProblemBlock`), stacked, each holding its own
 * evidence and its own fix side by side — so a plan carrying three problems
 * cannot leave the reader matching a fix to a problem by vertical position.
 * Beneath them the card's tail runs full width: what closes this (once a fix
 * is applied) and the blocked-on log.
 *
 * Deliberate absences:
 * - **No close button.** Both closed statuses are machine closes, so the only
 *   way out of an incident is a fix that verifies. A signal you believe is
 *   wrong gets SILENCED, which defers rather than concludes.
 * - **No "Needs attention" heading, no incident id, no status chip in the
 *   header.** A card with a red rail and a days-open counter does not need to
 *   announce that it needs attention; the id is not something anyone types;
 *   and the status is already on the problem it describes.
 * - **No Diagnose now.** A rules signal triggers its own Validator run; the
 *   manual re-run is a developer instrument and lives in the diagnostics
 *   block at the bottom of the page.
 *
 * - **No standing-signal panel** (removed 2026-08-14). It restated the failing
 *   rules that opened this incident, which the Health rules card below already
 *   carries with their windows, thresholds and history. `failingRules` survives
 *   here because the card's HEADLINE needs it in the one case where there is no
 *   incident yet: the rules have fired and the derivation has not run.
 * - **No "What moves this out of the queue"**. The gate — someone acts — is
 *   taught in person rather than reprinted under every open incident.
 */

/** Outlined, not filled: silencing is the quiet option beside logging a fix,
 *  and a solid button on it read as the recommended act. */
const SILENCE_BTN =
  "px-3.5 py-2 text-sm font-semibold rounded-md border border-brand-primary bg-white text-brand-primary hover:bg-[#E7F1F3] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2";
const NOTE_BTN =
  "px-3 py-1.5 text-xs font-semibold rounded-md bg-brand-primary text-white shadow-sm hover:bg-brand-primary-hover disabled:opacity-60";

type ModalState =
  | { kind: "silence" }
  /** `problem` set = a note scoped to one entry on the plan; it groups under
   *  that problem in the blocked-on log and resolves nothing. */
  | { kind: "note"; problem?: PlanProblemView }
  | { kind: "step-note"; step: PlanStepView; action: FixStepAction }
  /** The write confirmation (backend issue 50). `thenExecute` distinguishes
   *  approve-then-execute from executing a step already approved. */
  | { kind: "step-execute"; step: PlanStepView; thenExecute: boolean }
  | { kind: "accept-condition"; problem: PlanProblemView }
  | { kind: "log-fix"; problem: PlanProblemView }
  | { kind: "hand-fix" }
  | { kind: "edit-entry"; event: IncidentEventRow }
  | { kind: "remove-entry"; event: IncidentEventRow }
  | null;

export function IncidentPanel({ hotel }: { hotel: HotelDetail }) {
  const incidentLite = hotel.openIncident;
  const incidentQuery = useIncident(incidentLite?.id ?? "");
  const incident = incidentQuery.data;
  const [modal, setModal] = useState<ModalState>(null);

  /**
   * What measures this hotel, reduced to what the problem and fix paragraphs
   * branch on. `HotelDetail` carries the three facts inline, so it satisfies
   * `HotelTrackingContext` structurally — no separate fetch, and the card can
   * never render against a hotel other than the one the page is showing.
   */
  const copyContext = useMemo(() => hotelCopyContext(hotel), [hotel]);

  const stepAction = usePlanStepAction(hotel.customerId, incidentLite?.id);
  const silence = useSilenceIncident(hotel.customerId, incidentLite?.id);
  const clearSilence = useClearSilence(hotel.customerId, incidentLite?.id);
  const investigate = useInvestigate(hotel.customerId, incidentLite?.id);
  const addCondition = useAddCondition(hotel.customerId);
  const logNote = useLogNote(hotel.customerId, incidentLite?.id);
  const fixLog = useFixLog(hotel.customerId, incidentLite?.id);
  const handFix = useIncidentTransition(hotel.customerId, incidentLite?.id);
  const editEntry = useEditIncidentNote(hotel.customerId, incidentLite?.id);
  const removeRow = useRemoveHistoryRow(hotel.customerId, incidentLite?.id);

  /**
   * What's wrong, as the operator must read it: a rule counts as failing while
   * its warning STANDS, not only on the day its evaluation says `fail`.
   *
   * Reading `result` alone is what produced "No health rule is failing right
   * now" over an open incident — true of today's facts, and the worst possible
   * thing to tell someone triaging a broken hotel. Rule 2 is edge-triggered
   * (the run that fires it advances its own baseline, so the same $0 cell can
   * never fire twice) and a fix applied resets the trusted data start date,
   * blinding the windowed rules — so a passing read is routine while the
   * problem stands, and only an admin ends it.
   */
  const failingRules: RuleRow[] = hotel.rules.filter(
    (rule) =>
      (rule.standingWarning?.unresolved ||
        (rule.result === "fail" && !rule.blindWindow)) &&
      !rule.mapsToAcceptedCondition,
  );

  const activePlan = useMemo(() => {
    if (!incident) return null;
    return (
      incident.plans.find((plan) => plan.id === incident.activePlanId) ??
      incident.plans.find((plan) => plan.status === "active") ??
      null
    );
  }, [incident]);
  const displayPlan = activePlan ?? incident?.plans[0] ?? null;
  const latestBrief = incident?.briefs[0] ?? null;

  /**
   * Open problems first, then resolved ones — a stable sort, so catalog order
   * survives within each group. A resolved problem is not removed from the
   * card: it is the record that the work was done, and hiding it is how the
   * same fix gets logged twice.
   */
  const sortedProblems = useMemo(() => {
    const problems = displayPlan?.problems ?? [];
    return problems
      .map((problem, index) => ({ problem, index }))
      .sort(
        (a, b) =>
          (a.problem.resolution === "open" ? 0 : 1) -
            (b.problem.resolution === "open" ? 0 : 1) || a.index - b.index,
      )
      .map((entry) => entry.problem);
  }, [displayPlan]);

  /**
   * Fix reports, indexed by the problem they name.
   *
   * The server writes a fix report AND a blocked-on note from one gesture, so
   * these used to render in the Blocked-on list — under a heading meaning the
   * opposite of what they say. A fix report records that something was done;
   * blocked-on records what is still owed. They live on their problem now, and
   * only a report naming no problem on this plan falls back to the note list
   * (which is exactly the no-plan case, where the report IS the note).
   */
  const fixReportsByProblem = useMemo(() => {
    const byProblem = new Map<string, IncidentEventRow[]>();
    (incident?.events ?? [])
      .filter((event) => event.type === "fix-logged")
      .slice()
      .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
      .forEach((event) => {
        const problemId = problemIdOf(event);
        if (!problemId) return;
        const list = byProblem.get(problemId) ?? [];
        list.push(event);
        byProblem.set(problemId, list);
      });
    return byProblem;
  }, [incident]);

  /** Problem id → its title, for labelling a scoped note in the blocked-on
   *  log. The plan's own words, never a second vocabulary. */
  const problemTitles = useMemo(() => {
    const titles = new Map<string, string>();
    (displayPlan?.problems ?? []).forEach((problem) =>
      titles.set(problem.problemId, problem.title),
    );
    return titles;
  }, [displayPlan]);

  const notes = useMemo(() => {
    const onPlan = new Set<string>(
      (displayPlan?.problems ?? []).map((p) => p.problemId),
    );
    return (incident?.events ?? [])
      .filter(
        (event) =>
          event.type === "note" ||
          (event.type === "fix-logged" &&
            !onPlan.has(problemIdOf(event) ?? "")),
      )
      .slice()
      .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  }, [incident, displayPlan]);

  /**
   * A fix report the machine answered by reopening the incident.
   *
   * This needs saying out loud, because the whole round trip completes inside
   * one request: report accepted, every problem resolved, fix applied, the
   * corroboration run fires, a check finds something, and the incident is back
   * to open with a fresh plan — all before the page refetches. Afterwards the
   * card looks untouched except for a `reopened ×N` counter in six-point grey,
   * and the natural response to a button that appears to have done nothing is
   * to press it again. (Measured on a live incident: four reports in 70
   * seconds.)
   *
   * The distinction the banner turns on is what the run actually found. A
   * reopen does NOT mean the report was wrong — the reopen event's own reason
   * says "either the fix did not hold or something new broke". When the
   * problems it found are ones nobody just reported, the reports held and a
   * DIFFERENT problem reopened the incident, which is a completely different
   * thing to tell someone.
   */
  const overturn = useMemo(() => {
    const events = incident?.events ?? [];
    const reopens = events
      .filter((event) => event.type === "reopened")
      .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
    const latest = reopens[0];
    if (!latest) return null;

    const at = Date.parse(latest.at);
    const previous = reopens[1] ? Date.parse(reopens[1].at) : 0;
    // The reports this reopen answered: everything logged since the previous
    // reopen. Anything older belongs to a round already concluded.
    const reported = events.filter(
      (event) =>
        event.type === "fix-logged" &&
        Date.parse(event.at) <= at &&
        Date.parse(event.at) > previous,
    );
    if (reported.length === 0) return null;

    const rawProblems = latest.evidence?.problems;
    const found = Array.isArray(rawProblems)
      ? (rawProblems as unknown[]).filter(
          (id): id is string => typeof id === "string",
        )
      : [];
    const reportedIds = new Set(
      reported.map(problemIdOf).filter((id): id is string => id !== null),
    );
    return {
      event: latest,
      found,
      reportedIds,
      /** The run re-found something that was just reported fixed. */
      contradicted: found.some((id) => reportedIds.has(id)),
    };
  }, [incident]);

  const activeSilence =
    incidentLite?.silence?.state === "active" ? incidentLite.silence : null;

  // Nothing needs attention — say so in one quiet line and get out of the way.
  if (!incidentLite && failingRules.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] px-4 py-3 flex items-center gap-2.5 text-sm">
        <span className="w-2 h-2 rounded-full bg-success-dark flex-none" />
        <span className="text-content-secondary">
          Nothing needs attention — no failing health rules, no open incident.
        </span>
      </div>
    );
  }

  const onStepAction = (
    step: PlanStepView,
    action: FixStepAction,
    opts?: {
      thenExecute?: boolean;
      requireNote?: boolean;
      confirmExecute?: boolean;
    },
  ) => {
    if (opts?.requireNote) {
      setModal({ kind: "step-note", step, action });
      return;
    }
    // The write goes through a confirmation naming the change. Every other
    // action here edits a record we keep; this one edits the customer's
    // account, so it is the one that asks first.
    if (opts?.confirmExecute) {
      setModal({
        kind: "step-execute",
        step,
        thenExecute: opts.thenExecute === true,
      });
      return;
    }
    stepAction.mutate({
      stepIndex: step.index,
      action,
      thenExecute: opts?.thenExecute,
    });
  };

  const planActive =
    !!incident &&
    !!displayPlan &&
    displayPlan.status === "active" &&
    incident.statusClass !== "closed";
  /** The incident is in the work queue — the only class a fix can move. */
  const planActiveClass = !!incident && incident.statusClass === "active";

  // The card's one-line title: what this hotel's problem IS. An undiagnosed
  // incident withholds the stand-in issue (§5.1 rule 7) and says so.
  const headline = incidentLite
    ? incidentLite.undiagnosed
      ? "Signal confirmed — not yet diagnosed"
      : (incidentLite.primaryIssue ?? "Diagnosis in progress")
    : `${failingRules.map((rule) => `Rule ${rule.rule}`).join(", ")} failing`;

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border border-[#E9E9EB] border-l-4 border-l-danger-dark">
      {/* Header: how long, what it is, and the two acts. The buttons carry no
          panel of their own — a tray around two buttons was one more box on a
          page made of boxes. */}
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-4 flex-wrap border-b border-[#E9E9EB]">
        <div className="flex items-start gap-4 min-w-0">
          {incidentLite && (
            <div className="text-center shrink-0 pt-0.5">
              <div
                className={`text-[32px] leading-none font-semibold ${
                  incidentLite.statusClass === "active"
                    ? "text-danger-dark"
                    : "text-content-primary"
                }`}
              >
                {incidentLite.daysOpen}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">
                {incidentLite.daysOpen === 1 ? "day" : "days"} open
              </div>
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-content-primary">
              {headline}
            </h3>
            <div className="text-xs text-gray-400 mt-1">
              {incidentLite ? (
                <>
                  {incident?.origin ?? "open incident"}
                  {incidentLite.openedAt && (
                    <> · since {fmtDate(incidentLite.openedAt)}</>
                  )}
                  {incident && incident.reopenedCount > 0 && (
                    <> · reopened ×{incident.reopenedCount}</>
                  )}
                </>
              ) : (
                <>
                  no incident open yet — the daily derivation opens one on its
                  next pass. The evidence is on the Health rules card below.
                </>
              )}
            </div>
          </div>
        </div>
        {/* No Log fix button here since 2026-08-13: a fix is logged AGAINST a
            problem, so the button belongs on the problem it resolves. One at
            the top meant choosing from a picklist of sixteen the problem you
            were already looking at. */}
        {incidentLite && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* The hint rides the span, not the button: a disabled control
                fires no mouse events, and "already silenced until …" is
                exactly the hint you want while it is disabled. */}
            <span
              className="inline-flex"
              data-tip={
                activeSilence
                  ? `Already silenced until ${fmtDate(activeSilence.until)}`
                  : "Suspect the signal is wrong, or can't judge yet? Go quiet until a date — the incident stays open, the hotel stays Unhealthy, and every rule keeps running"
              }
            >
              <button
                onClick={() => setModal({ kind: "silence" })}
                className={SILENCE_BTN}
                disabled={!!activeSilence}
              >
                <span aria-hidden>🔕</span>
                {activeSilence ? "Silenced" : "Silence this incident…"}
              </button>
            </span>
          </div>
        )}
      </div>

      {/* A standing silence is the loudest thing on the card. It has to be:
          the reminders an operator would otherwise expect are not coming, and
          nothing else on the page says so. */}
      {activeSilence && (
        <div className="px-4 py-3 bg-warning-light border-b border-warning-dark/25 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl leading-none" aria-hidden>
              🔕
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-warning-dark">
                Silenced until {fmtDate(activeSilence.until)} ·{" "}
                {activeSilence.daysRemaining}d left
              </div>
              <div className="text-xs text-warning-dark/80 mt-0.5">
                {fmtActor(activeSilence.silencedBy)}
                {activeSilence.cause && (
                  <> · {SILENCE_CAUSE_DISPLAY[activeSilence.cause]}</>
                )}
                {activeSilence.note && <> · “{activeSilence.note}”</>} — no
                reminders until then; the incident stays open and every rule
                keeps running.
              </div>
            </div>
          </div>
          <button
            onClick={() => clearSilence.mutate()}
            disabled={clearSilence.isPending}
            className="px-3 py-1.5 text-xs font-semibold rounded-md border border-warning-dark text-warning-dark hover:bg-warning-light disabled:opacity-50 whitespace-nowrap bg-white/60"
          >
            {clearSilence.isPending ? "Working…" : "Return to cadence"}
          </button>
        </div>
      )}

      {/* The body scrolls; the header and the silence banner do not — an
          operator must never lose the two actions to a long fix plan. With no
          incident yet there is no body at all: the header line already says
          which rules are failing and where their evidence is. */}
      {incidentLite && (
      <div className="px-4 py-4 overflow-y-auto max-h-[62vh]">
        {incidentQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
            <Spinner className="w-4 h-4" /> loading the incident's plan…
          </div>
        )}

        <div className="flex flex-col gap-4">
          {/* The system contradicted the operator. Nothing else on the page
              says so, and without it the whole gesture reads as a no-op: the
              fix is recorded, the incident bounces back to open in the same
              second, and the card looks exactly as it did before. */}
          {overturn && (
            <ReopenedBanner
              reopenedAt={overturn.event.at}
              contradicted={overturn.contradicted}
              found={overturn.found}
              runId={
                typeof overturn.event.evidence?.runId === "string"
                  ? overturn.event.evidence.runId
                  : null
              }
            />
          )}

          {/* One card per problem, its fix inside it. Resolved problems sort
              last and grey out rather than vanishing — they are the record of
              what was already done. */}
          {incident &&
            displayPlan &&
            sortedProblems.map((problem) => (
              <ProblemBlock
                key={problem.problemId}
                problem={problem}
                steps={displayPlan.steps}
                hotel={copyContext}
                incident={{
                  status: incident.status,
                  statusClass: incident.statusClass,
                }}
                planActive={planActive}
                fixReports={fixReportsByProblem.get(problem.problemId) ?? []}
                onAcceptCondition={(p) =>
                  setModal({ kind: "accept-condition", problem: p })
                }
                onStepAction={onStepAction}
                stepPending={stepAction.isPending}
                onLogFix={(p) => setModal({ kind: "log-fix", problem: p })}
                onAddNote={(p) => setModal({ kind: "note", problem: p })}
                onEditEntry={(event) => setModal({ kind: "edit-entry", event })}
                onRemoveEntry={(event) =>
                  setModal({ kind: "remove-entry", event })
                }
              />
            ))}

          {incident && displayPlan?.status === "failed" && (
            <div className="px-3 py-2 rounded-md bg-[#FEF3C7] text-xs text-warning-dark">
              This plan attempt failed (a reopen marks the active plan failed);
              it stays readable as the record of what was tried.
            </div>
          )}

          {/* Signal, but no diagnosed problem. Nothing is enumerated here, so
              there is nothing to log a fix AGAINST — the hand-recorded fix
              below is the route, and it is the one the spec always named as
              the recorded manual check. */}
          {incident && !displayPlan && (
            <div className="px-3 py-3 rounded-lg bg-warning-light/40 border border-warning-light">
              <div className="text-sm font-semibold text-warning-dark">
                {incident.undiagnosed
                  ? "A full diagnostic run found nothing actionable"
                  : "Not diagnosed yet"}
              </div>
              <p className="text-xs text-content-secondary mt-1">
                {incident.undiagnosed
                  ? "The signal stands and no check names a cause. The AI brief below is leads, never evidence — re-run the investigation, record a fix you made by hand, or silence the incident if you can't judge it yet."
                  : "A rules signal triggers its own run on the next daily pass; the manual re-run is in Developer diagnostics at the bottom of this page."}
              </p>
              {incident.warnings.length > 0 && (
                <div className="mt-2 pt-2 border-t border-warning-light">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                    The signal
                  </div>
                  {incident.warnings.map((warning) => (
                    <div key={warning.rule} className="py-0.5 text-xs">
                      <span className="font-medium text-content-primary">
                        {warning.title}
                      </span>{" "}
                      <span className="text-content-secondary">
                        — {warning.evidence}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {latestBrief && latestBrief.briefMarkdown && (
                <div className="mt-2 pt-2 border-t border-warning-light">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                    AI investigation brief · {latestBrief.model ?? "model"} ·{" "}
                    {fmtDate(latestBrief.generatedAt)} — leads, never evidence
                  </div>
                  <CollapsedBrief markdown={latestBrief.briefMarkdown} />
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-warning-light">
                {planActiveClass && (
                  <button
                    onClick={() => setModal({ kind: "hand-fix" })}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md border border-brand-primary text-brand-primary bg-white hover:bg-[#E7F1F3]"
                  >
                    Record a fix made by hand…
                  </button>
                )}
                {incident.undiagnosed && (
                  <button
                    onClick={() => investigate.mutate()}
                    disabled={investigate.isPending}
                    className="px-2.5 py-1 text-xs font-medium rounded border border-warning-dark text-warning-dark hover:bg-warning-light disabled:opacity-50"
                  >
                    {investigate.isPending
                      ? "Re-running the AI investigation…"
                      : "Re-run AI investigation"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* The card's tail, full width beneath the problems.
              It used to be a two-column row: a "standing signal" panel on the
              left restating the failing rules, and verification plus the
              blocked-on log on the right. The signal panel is gone — the rule
              that opened this is on the Health rules card below with its
              window, threshold and history, and repeating a shortened copy of
              it here made the operator read the same failure twice and gave
              the blocked-on log half a card to live in. */}
          {incident && (
            <>
              <NextStep incident={incident} />

              {/* Blocked-on gets the full width now, which is what makes the
                  per-problem scope legible: each entry can carry the problem
                  it answers on its own row instead of being squeezed out. */}
              <div className="rounded-lg border border-[#E9E9EB] bg-[#FAFAFB] p-3.5">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <span className="text-sm font-semibold text-content-primary">
                      Blocked on
                    </span>
                  </div>
                  <button
                    onClick={() => setModal({ kind: "note" })}
                    className={NOTE_BTN}
                    data-tip="A note about the incident as a whole — not about any one problem"
                  >
                    + Log a note
                  </button>
                </div>
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    Nothing logged yet — a note here is what tells the next
                    person what this is waiting on.
                  </p>
                ) : (
                  <div className="flex flex-col">
                    {notes.slice(0, 6).map((note, i) => (
                      <EntryLine
                        key={note.id}
                        event={note}
                        latest={i === 0}
                        problemTitle={problemTitles.get(
                          problemIdOf(note) ?? "",
                        )}
                        onEdit={() =>
                          setModal({ kind: "edit-entry", event: note })
                        }
                        onRemove={() =>
                          setModal({ kind: "remove-entry", event: note })
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {/* Modals */}
      {modal?.kind === "silence" && incidentLite && (
        <SilenceModal
          hotelName={hotel.hotelName}
          pending={silence.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) =>
            silence.mutate(body, { onSuccess: () => setModal(null) })
          }
        />
      )}
      {modal?.kind === "note" && (
        <NoteModal
          title={
            modal.problem ? `Note — ${modal.problem.title}` : "Log a note"
          }
          problemTitle={modal.problem?.title}
          pending={logNote.isPending}
          onClose={() => setModal(null)}
          onSubmit={(note) =>
            logNote.mutate(
              { note, problem: modal.problem?.problemId },
              { onSuccess: () => setModal(null) },
            )
          }
        />
      )}
      {modal?.kind === "log-fix" && (
        <LogFixModal
          problem={{
            problemId: modal.problem.problemId,
            title: modal.problem.title,
          }}
          pending={fixLog.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) =>
            fixLog.mutate(body, { onSuccess: () => setModal(null) })
          }
        />
      )}
      {modal?.kind === "hand-fix" && (
        <HandFixModal
          pending={handFix.isPending}
          onClose={() => setModal(null)}
          onSubmit={(note) =>
            handFix.mutate(
              {
                to: "fix_applied",
                reason:
                  "fix recorded by hand — this incident carries no fix plan, " +
                  "so there is no enumerated problem to log against; the " +
                  "operator's own check is the record",
                note,
              },
              { onSuccess: () => setModal(null) },
            )
          }
        />
      )}
      {modal?.kind === "edit-entry" && (
        <EditEntryModal
          kind={modal.event.type === "fix-logged" ? "fix-logged" : "note"}
          current={modal.event.reason}
          pending={editEntry.isPending}
          onClose={() => setModal(null)}
          onSubmit={(note) =>
            editEntry.mutate(
              { eventId: modal.event.id, note },
              { onSuccess: () => setModal(null) },
            )
          }
        />
      )}
      {modal?.kind === "remove-entry" && (
        <RemoveHistoryRowModal
          log="incident-event"
          label={
            modal.event.type === "fix-logged" ? "Fix logged" : "Blocked-on note"
          }
          detail={modal.event.reason}
          pending={removeRow.isPending}
          onClose={() => setModal(null)}
          onSubmit={(reason) =>
            removeRow.mutate(
              { log: "incident-event", rowId: modal.event.id, reason },
              { onSuccess: () => setModal(null) },
            )
          }
        />
      )}
      {modal?.kind === "step-note" && (
        <StepNoteModal
          action={modal.action}
          byHand={
            modal.action === "complete" && modal.step.kind === "automatic"
          }
          pending={stepAction.isPending}
          onClose={() => setModal(null)}
          onSubmit={(note) =>
            stepAction.mutate(
              { stepIndex: modal.step.index, action: modal.action, note },
              { onSuccess: () => setModal(null) },
            )
          }
        />
      )}
      {modal?.kind === "step-execute" && (
        <ExecuteStepModal
          step={modal.step}
          pending={stepAction.isPending}
          onClose={() => setModal(null)}
          onSubmit={() =>
            stepAction.mutate(
              {
                stepIndex: modal.step.index,
                action: modal.thenExecute ? "approve" : "execute",
                thenExecute: modal.thenExecute,
              },
              // Only on success: a refused or rejected write leaves the modal
              // open with the server's own sentence in the toast, so the
              // operator reads why rather than watching it vanish.
              { onSuccess: () => setModal(null) },
            )
          }
        />
      )}
      {modal?.kind === "accept-condition" && (
        <AcceptConditionModal
          scopeType="problem"
          scopeId={modal.problem.problemId}
          scopeLabel={modal.problem.title}
          pending={addCondition.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) =>
            addCondition.mutate(body, { onSuccess: () => setModal(null) })
          }
        />
      )}
    </div>
  );
}

/**
 * One operator-authored entry — a note or a logged fix — with the two things
 * that can be done to it. Both actions are held back until hover: a log is for
 * reading, and a row wearing two buttons at rest invites tidying rather than
 * recording.
 *
 * The edit and remove buttons mean different things and are worded to say so:
 * editing changes the words and nothing else, removing hides the row from this
 * page while the machine goes on seeing it.
 */
function EntryLine({
  event,
  latest,
  problemTitle,
  onEdit,
  onRemove,
}: {
  event: IncidentEventRow;
  latest: boolean;
  /** The plan problem this entry names, when it names one. */
  problemTitle?: string;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const isFix = event.type === "fix-logged";
  return (
    <div className="group py-1.5 border-b border-[#E9E9EB] last:border-b-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {isFix && (
            <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-success-light text-success-dark align-[2px]">
              fix logged
            </span>
          )}
          {problemTitle && (
            <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#EEF1F4] text-content-secondary align-[2px]">
              {problemTitle}
            </span>
          )}
          <span
            className={`text-sm ${
              latest
                ? "font-semibold text-content-primary"
                : "text-content-secondary"
            }`}
          >
            {event.reason}
          </span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity shrink-0">
          <button
            onClick={onEdit}
            className="px-1.5 py-0.5 text-[11px] font-medium rounded text-gray-500 hover:text-content-primary hover:bg-gray-100"
            data-tip="Correct the wording — nothing re-runs, the reminder clock does not move"
          >
            Edit
          </button>
          <button
            onClick={onRemove}
            className="px-1.5 py-0.5 text-[11px] font-medium rounded text-gray-400 hover:text-danger-dark hover:bg-danger-light/50"
            data-tip="Hide this row from the history (it changes nothing else)"
          >
            Remove
          </button>
        </div>
      </div>
      <div className="text-xs text-gray-400 mt-0.5">
        {fmtDate(event.at)} · {fmtActor(event.actor)}
        {event.editedAt && (
          <span data-tip={`edited by ${fmtActor(event.editedBy) || "an admin"}`}>
            {" "}
            · edited
          </span>
        )}
      </div>
    </div>
  );
}

/** A `fix-logged` event names its problem in evidence; nothing else does. */
function problemIdOf(event: IncidentEventRow): string | null {
  const problem = event.evidence?.problem;
  return typeof problem === "string" ? problem : null;
}

/**
 * The check that answered a fix report reopened the incident — in the two
 * quite different senses that can carry.
 *
 * **Contradicted**: the run re-found a problem that was just reported fixed.
 * The report has been overturned, re-logging it will produce the same result,
 * and the account itself has to change.
 *
 * **Something else**: the reports held, and the run found a DIFFERENT problem,
 * which is what reopened the incident. Telling that operator their fix failed
 * would be false and would send them back to redo work that landed.
 *
 * Both are loud, because both look identical from the operator's chair — an
 * unchanged card and a small `reopened ×N`.
 */
function ReopenedBanner({
  reopenedAt,
  contradicted,
  found,
  runId,
}: {
  reopenedAt: string;
  contradicted: boolean;
  found: string[];
  runId: string | null;
}) {
  const names = found
    .map((id) => PROBLEM_SHORT_LABELS[id as ProblemId] ?? `Problem ${id}`)
    .join(", ");
  return (
    <div
      className={clsx(
        "rounded-lg border px-4 py-3",
        contradicted
          ? "border-danger-light bg-danger-light/40"
          : "border-warning-dark/25 bg-warning-light/50",
      )}
    >
      <div className="flex items-center gap-2">
        <span aria-hidden>{contradicted ? "⚠️" : "ℹ️"}</span>
        <span
          className={clsx(
            "text-sm font-semibold",
            contradicted ? "text-danger-dark" : "text-warning-dark",
          )}
        >
          {contradicted
            ? "Your fix report was overturned — the problem is open again"
            : "Your fix was recorded and held — a different problem reopened this"}
        </span>
      </div>
      <p className="text-xs text-content-secondary mt-1.5">
        {contradicted ? (
          <>
            A report stands until a check contradicts it. The check ran{" "}
            {fmtDate(reopenedAt)} against the live account and still found
            {names ? <> {names}</> : <> the problem</>} — so whatever was
            reported has not landed there yet.{" "}
            <span className="font-medium">
              Logging it again will produce the same result.
            </span>{" "}
            Change the account itself, or use the plan's own step where it has
            one.
          </>
        ) : (
          <>
            Everything you reported was accepted and none of it was re-found.
            The check that ran afterwards, on {fmtDate(reopenedAt)}, turned up
            {names ? <> {names}</> : <> something else</>} — a problem nobody
            had reported — and an incident stays open while any problem stands.
            The new one is below; nothing needs redoing.
          </>
        )}
      </p>
      {runId && (
        <Link
          to={`/tracking/health/runs/${runId}`}
          className="inline-block text-xs font-medium text-brand-primary hover:text-brand-primary-hover mt-1.5"
        >
          see exactly what the run checked →
        </Link>
      )}
    </div>
  );
}

/**
 * What closes this incident — rendered ONLY once a fix is applied and the
 * hotel is proving itself.
 *
 * There used to be a second branch for the active case, headed "What moves
 * this out of the queue", spelling out that someone acting is the whole gate.
 * It was true and it was removed on purpose: the mechanism is being taught to
 * the people who use this in person, and a permanent explainer under every
 * open incident charges the tenth reading the same cost as the first.
 *
 * The two health transitions are still not one gate — someone acting moves a
 * hotel out of Unhealthy (ADR 0018), a real ad-driven booking closes the
 * incident afterwards — which is exactly why the booking checklist may only
 * render on this side of the fix. Shown over an active incident it ticked
 * green off a count that predates the fix and is about to be reset to zero
 * by it.
 */
function NextStep({ incident }: { incident: IncidentDetail }) {
  if (incident.statusClass !== "awaiting-verification") return null;

  const proof = incident.verification.proof;
  // Never round UP: `met` is a sum of fractionally-attributed conversions, and
  // a display that reads `1 of 1` beside an unmet verdict is worse than a
  // blunt one. Flooring can only ever understate.
  const met = Math.floor(proof.met);
  const bookings = `${proof.required} qualifying booking${proof.required === 1 ? "" : "s"}`;

  return (
    <div>
      <TileLabel>What closes this</TileLabel>
      <ChecklistRow
        done={proof.met >= proof.required}
        label={`Real booking — ${met} of ${bookings} since the trusted data start date`}
      />
      <p className="text-xs text-gray-400 mt-1">
        The fix is recorded
        {incident.fixAppliedAt && <> ({fmtDate(incident.fixAppliedAt)})</>} and
        this hotel is in Monitoring while it proves itself. Only a booking
        clicked on or after
        {incident.verification.trustedDataStartDate ? (
          <> {fmtDate(incident.verification.trustedDataStartDate)}</>
        ) : (
          <> the trusted data start date</>
        )}{" "}
        counts; the daily pass closes this the moment one lands.
      </p>
    </div>
  );
}

function ChecklistRow({ done, label }: { done: boolean; label: string }) {
  return (
    <div className="flex items-start gap-2 py-1">
      <span
        className={`w-4 h-4 mt-0.5 rounded-full flex items-center justify-center text-[10px] shrink-0 ${
          done
            ? "bg-success-light text-success-dark"
            : "bg-[#F4F4F6] text-gray-400"
        }`}
      >
        {done ? "✓" : "·"}
      </span>
      <span className="text-sm text-content-secondary">{label}</span>
    </div>
  );
}
